import mermaid from 'mermaid';
import extensionSvc from '../services/extensionSvc';
import utils from '../services/utils';
import store from '../store';

// Get Mermaid theme based on app theme
const getMermaidTheme = () => {
  const appTheme = store.getters['data/computedSettings']?.colorTheme || 'light';
  const darkThemes = ['dark', 'nord', 'solarized-dark', 'dracula', 'monokai'];
  
  if (darkThemes.includes(appTheme)) {
    return 'dark';
  }
  if (appTheme === 'forest') {
    return 'forest';
  }
  return 'default';
};

// Track rendered diagrams for re-rendering on theme change
const renderedDiagrams = new Set();

const getConfig = () => ({
  logLevel: 5,
  startOnLoad: false,
  arrowMarkerAbsolute: false,
  theme: getMermaidTheme(),
  flowchart: {
    htmlLabels: true,
    curve: 'linear',
  },
  sequence: {
    diagramMarginX: 50,
    diagramMarginY: 10,
    actorMargin: 50,
    width: 150,
    height: 65,
    boxMargin: 10,
    boxTextMargin: 5,
    noteMargin: 10,
    messageMargin: 35,
    mirrorActors: true,
    bottomMarginAdj: 1,
    useMaxWidth: true,
  },
  gantt: {
    titleTopMargin: 25,
    barHeight: 20,
    barGap: 4,
    topPadding: 50,
    leftPadding: 75,
    gridLineStartPadding: 35,
    fontSize: 11,
    fontFamily: '"Open-Sans", "sans-serif"',
    numberSectionStyles: 4,
    axisFormat: '%Y-%m-%d',
  },
});

const containerElt = document.createElement('div');
containerElt.className = 'hidden-rendering-container';
document.body.appendChild(containerElt);

let lastTheme = null;

const render = async (elt) => {
  try {
    // Re-initialize if theme changed
    const currentTheme = getMermaidTheme();
    if (currentTheme !== lastTheme) {
      mermaid.initialize(getConfig());
      lastTheme = currentTheme;
    }
    
    // Store original source for re-rendering
    if (!elt.dataset.mermaidSource) {
      elt.dataset.mermaidSource = elt.textContent;
    }
    
    const svgId = `mermaid-svg-${utils.uid()}`;
    const { svg } = await mermaid.render(svgId, elt.dataset.mermaidSource);
    elt.innerHTML = svg;
    
    // Track this element for re-rendering on theme change
    renderedDiagrams.add(elt);
  } catch (e) {
    console.error(e); // eslint-disable-line no-console, no-undef
  }
};

// Re-render all diagrams when theme changes
const reRenderAll = async () => {
  const currentTheme = getMermaidTheme();
  if (currentTheme !== lastTheme) {
    mermaid.initialize(getConfig());
    lastTheme = currentTheme;
    
    // Re-render all tracked diagrams
    renderedDiagrams.forEach((elt) => {
      if (elt.dataset.mermaidSource && document.body.contains(elt)) { // eslint-disable-line no-undef
        const svgId = `mermaid-svg-${utils.uid()}`;
        mermaid.render(svgId, elt.dataset.mermaidSource)
          .then(({ svg }) => {
            elt.innerHTML = svg;
          })
          .catch((e) => console.error(e)); // eslint-disable-line no-console, no-undef
      } else {
        renderedDiagrams.delete(elt);
      }
    });
  }
};

// Watch for theme changes
store.watch(
  () => store.getters['data/computedSettings']?.colorTheme,
  () => reRenderAll(),
);

extensionSvc.onGetOptions((options, properties) => {
  options.mermaid = properties.extensions.mermaid.enabled;
});

extensionSvc.onSectionPreview((elt) => {
  elt.querySelectorAll('.prism.language-mermaid')
    .cl_each(diagramElt => render(diagramElt.parentNode));
});
